import React, { useCallback, useEffect, useState } from 'react';
import axiosPrivate from 'config/axiosPrivate';

import { useDispatch } from 'react-redux';
import { addUser, updateUser } from 'store/Admin/UserManagement';

import { PWD_REGEX, EMAIL_REGEX, PHONENUMBER_REGEX } from 'utils/Regex';
import { LANGUAGES, ROLES } from 'utils/Constants';

import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import { Modal } from 'reactstrap';

import { classes } from 'components/shared/modal/modalClasses';
import { useTranslation } from 'react-i18next';
import { UserLanguageSelector } from '../UserLanguageSelector';

const API = {
  createConsultant: '/user/',
  updateConsultant: '/user/',
};

export const ConsultantModal = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [mail, setMail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [selectedLanguage, setSelectedLanguage] = useState(null);

  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const [disabledForm, setDisabledForm] = useState(true);

  useEffect(() => {
    if (props && props.user) {
      setName(props.user.name);
      setMail(props.user.mail);
      setPhoneNumber(props.user.phoneNumber);

      const lang = LANGUAGES.find(
        (l) => l.isoCode === props.user.isoLanguageCode
      );
      setSelectedLanguage(lang || null);
    }
  }, [props]);

  const checkPasswordValid = useCallback(() => {
    return !password || PWD_REGEX.test(password);
  }, [password]);

  const checkValidForm = useCallback(() => {
    if (!mail || !phoneNumber || !name || !selectedLanguage) {
      setDisabledForm(true);
      return;
    }

    if (!mail.match(EMAIL_REGEX)) {
      setDisabledForm(true);
      return;
    }

    return props.user
      ? setDisabledForm(!checkPasswordValid())
      : setDisabledForm(false);
  }, [
    checkPasswordValid,
    mail,
    name,
    phoneNumber,
    selectedLanguage,
    props.user,
  ]);

  useEffect(() => {
    checkValidForm();
  }, [mail, phoneNumber, password, name, selectedLanguage, checkValidForm]);

  const updateConsultant = () => {
    let body = {
      password: !password ? null : password,
      mail,
      name,
      phoneNumber,
      role: ROLES.CONSULTANT,
      isoLanguageCode: selectedLanguage.isoCode,
    };

    axiosPrivate
      .put(API.updateConsultant, body, { params: { mail: props.user.mail } })
      .then((res) => {
        dispatch(updateUser({ ...res.data }));
        setIsSuccess(true);
        setErrorSuccessMsg(
          `${t('Le consultant')} ${props.user.mail} ${t(
            'a été mis à jour avec succès'
          )} !`
        );

        setErrorSuccess(true);
      })
      .catch((e) => {
        setIsSuccess(false);
        switch (e.response.status) {
          case 409:
            setErrorSuccessMsg(
              `${t("Un utilisateur existe déjà avec l'email")} ${mail}`
            );
            break;
          default:
            setErrorSuccessMsg(t("Oups....! Une erreur s'est produite"));
            break;
        }
        setErrorSuccess(true);
      });
  };

  const createConsultant = () => {
    let body = {
      mail,
      name,
      phoneNumber,
      role: ROLES.CONSULTANT,
      isoLanguageCode: selectedLanguage.isoCode,
    };

    axiosPrivate
      .post(API.createConsultant, body)
      .then((res) => {
        dispatch(addUser({ ...res.data, role: ROLES.CONSULTANT }));
        setIsSuccess(true);

        setErrorSuccessMsg(
          `${t('Le consultant')} ${mail} ${t('a été créé avec succès')} !`
        );

        setErrorSuccess(true);
      })
      .catch((e) => {
        setIsSuccess(false);
        switch (e.response.status) {
          case 409:
            setErrorSuccessMsg(
              `${t("Un utilisateur existe déjà avec l'email")} ${mail}`
            );
            break;
          default:
            setErrorSuccessMsg(t("Oups....! Une erreur s'est produite"));
            break;
        }
        setErrorSuccess(true);
      });
  };

  if (props.user)
    return (
      <div>
        <Modal style={classes.modalCustom} isOpen={props.open}>
          <div style={classes.modalHeader} className="modal-header">
            <h5 style={classes.title} className="modal-title">
              {t('Modifier le consultant')}
            </h5>
            <button
              type="button"
              onClick={props.handleClose}
              style={classes.btnClose}
              className="btn-close"
            />
          </div>
          <div style={classes.modalPad} className="modal-body">
            <div className="row row-mb-lg">
              <div className="col-12 col-md-12">
                <div className="label-title">{t('Prénom/Nom')}</div>
                <div>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="form-control form-ct"
                  />
                </div>
              </div>
            </div>
            <div className="row row-mb-lg">
              <div className="col-12 col-md-12">
                <div className="label-title">{t('E - mail')}</div>
                <div>
                  <input
                    value={mail}
                    onChange={(e) => setMail(e.target.value)}
                    type="text"
                    className="form-control form-ct"
                  />
                </div>
              </div>
            </div>
            <div className="row row-mb-lg">
              <div className="col-12 col-md-12">
                <UserLanguageSelector
                  selectedLanguage={selectedLanguage}
                  onLanguageChange={(option) => setSelectedLanguage(option)}
                />
              </div>
            </div>
            <div className="row row-mb-lg">
              <div className="col-12 col-md-6">
                <div className="label-title">{t('Téléphone')}</div>
                <div>
                  <input
                    value={phoneNumber}
                    onChange={(e) => {
                      if (
                        e.target.value === '' ||
                        PHONENUMBER_REGEX.test(e.target.value)
                      ) {
                        setPhoneNumber(e.target.value);
                      }
                    }}
                    type="text"
                    className="form-control form-ct"
                  />
                </div>
              </div>
            </div>
            <div className="row row-mb-lg">
              <div className="col-12 col-md-12">
                <div className="label-title">{t('Mot de passe')}*</div>
                <div className="input-group input-password">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-control"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <span
                    onClick={() => setShowPassword(!showPassword)}
                    className="input-group-text"
                  >
                    {showPassword ? (
                      <img
                        src="/Images/icon-eye-black.png"
                        alt="eye black icon"
                      />
                    ) : (
                      <img
                        src="/Images/icon-eye-gray.png"
                        alt="eye gray icon"
                      />
                    )}
                  </span>
                </div>
              </div>
              <div className="text-more mt-2">
                *
                {t(
                  'Avoir au moins 8 caractères avec une lettre majuscule, un chiffre, un symbole et ne pas être identique au nom.'
                )}
              </div>
            </div>
            <div className="box-button-bt">
              <div>
                {disabledForm ? (
                  <button className="bt-submit-disabled-y float-end">
                    {t('Sauvegarder')}
                  </button>
                ) : (
                  <button
                    className="bt-submit-y float-end"
                    onClick={updateConsultant}
                  >
                    {t('Sauvegarder')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </Modal>
        <ErrorSuccesModal
          open={errorSuccess}
          message={errorSuccessMsg}
          handleClose={() => {
            if (isSuccess === true) props.handleClose();
            setErrorSuccess(false);
          }}
          success={isSuccess}
          toggle={() => setErrorSuccess(!errorSuccess)}
        />
      </div>
    );

  return (
    <div>
      <Modal style={classes.modalCustom} isOpen={props.open}>
        <div style={classes.modalHeader} className="modal-header">
          <h5 style={classes.title} className="modal-title">
            {t('Ajouter un consultant')}
          </h5>
          <button
            type="button"
            onClick={props.handleClose}
            style={classes.btnClose}
            className="btn-close"
          />
        </div>
        <div style={classes.modalPad} className="modal-body">
          <div className="row row-mb-lg">
            <div className="col-12 col-md-12">
              <div className="label-title">{t('Prénom/Nom')}</div>
              <div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="form-control form-ct"
                />
              </div>
            </div>
          </div>
          <div className="row row-mb-lg">
            <div className="col-12 col-md-12">
              <div className="label-title">{t('E - mail')}</div>
              <div>
                <input
                  value={mail}
                  onChange={(e) => setMail(e.target.value)}
                  type="text"
                  className="form-control form-ct"
                />
              </div>
            </div>
          </div>
          <div className="row row-mb-lg">
            <div className="col-12 col-md-12">
              <UserLanguageSelector
                selectedLanguage={selectedLanguage}
                onLanguageChange={(option) => setSelectedLanguage(option)}
              />
            </div>
          </div>
          <div className="row row-mb-lg">
            <div className="col-12 col-md-6">
              <div className="label-title">{t('Téléphone')}</div>
              <div>
                <input
                  value={phoneNumber}
                  onChange={(e) => {
                    if (
                      e.target.value === '' ||
                      PHONENUMBER_REGEX.test(e.target.value)
                    ) {
                      setPhoneNumber(e.target.value);
                    }
                  }}
                  type="text"
                  className="form-control form-ct"
                />
              </div>
            </div>
          </div>

          <div className="box-button-bt">
            <div>
              {disabledForm ? (
                <button className="bt-submit-disabled-y float-end">
                  {t('Sauvegarder')}
                </button>
              ) : (
                <button
                  className="bt-submit-y float-end"
                  onClick={createConsultant}
                >
                  {t('Sauvegarder')}
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal>
      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        handleClose={() => {
          if (isSuccess === true) props.handleClose();
          setErrorSuccess(false);
        }}
        success={isSuccess}
        toggle={() => setErrorSuccess(!errorSuccess)}
      />
    </div>
  );
};

export default ConsultantModal;
